<script setup lang="ts">
    import { computed, nextTick, ref, watch } from 'vue';

    import { validateEmail } from '@/pages/Landing/utils/landingValidators';
    import { landingPageInquiryService } from '@/services/LandingPageInquiryService';

    const props = defineProps<{
        modelValue: boolean;
        initialEmail?: string;
    }>();

    const emit = defineEmits<{
        (e: 'update:modelValue', value: boolean): void;
    }>();

    const dialogValue = computed({
        get: () => props.modelValue,
        set: (value: boolean) => emit('update:modelValue', value),
    });

    const close = () => {
        dialogValue.value = false;
    };

    const emailInput = ref<HTMLInputElement | null>(null);
    const email = ref('');
    const message = ref('');
    const submitting = ref(false);
    const hasSubmitted = ref(false);
    const error = ref<string | null>(null);

    const validateMessage = (value: string): string | null => {
        const trimmed = value.trim();
        if (!trimmed) {
            return 'Message is required.';
        }

        if (trimmed.length > 5000) {
            return 'Message is too long.';
        }

        return null;
    };

    const canSubmit = computed(() => {
        return !submitting.value && validateEmail(email.value) === null && validateMessage(message.value) === null;
    });

    const resetState = () => {
        email.value = props.initialEmail?.trim() ?? '';
        message.value = '';
        submitting.value = false;
        hasSubmitted.value = false;
        error.value = null;
    };

    async function submit() {
        error.value = validateEmail(email.value) ?? validateMessage(message.value);
        if (error.value) return;

        submitting.value = true;
        try {
            await landingPageInquiryService.submitInquiry(email.value.trim(), message.value.trim());
            hasSubmitted.value = true;
        } catch (err: any) {
            error.value = err?.response?.data?.message ?? 'Unable to send your message. Please try again.';
        } finally {
            submitting.value = false;
        }
    }

    watch(
        () => dialogValue.value,
        (open) => {
            if (!open) return;
            resetState();
            nextTick(() => {
                emailInput.value?.focus();
            });
        },
    );
</script>

<template>
    <div v-if="dialogValue" class="contact-dialog__overlay" role="presentation">
        <div
            class="contact-dialog__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="landing-contact-title"
            aria-describedby="landing-contact-description"
            tabindex="-1"
            @keydown.esc="close">
            <button class="contact-dialog__close" type="button" aria-label="Close" @click="close">×</button>

            <div class="contact-dialog__top">
                <h3 id="landing-contact-title" class="contact-dialog__title">Contact</h3>
                <p id="landing-contact-description" class="contact-dialog__subtitle">Send a note to the Mix n' Batch team</p>
            </div>

            <div class="contact-dialog__body">
                <p v-if="hasSubmitted" class="contact-dialog__success" role="status">Thanks! Your message has been sent.</p>
                <template v-else>
                    <label class="contact-dialog__label" for="landing-contact-email">Email</label>
                    <input
                        id="landing-contact-email"
                        ref="emailInput"
                        v-model="email"
                        class="contact-dialog__input"
                        type="email"
                        name="email"
                        autocomplete="email"
                        inputmode="email"
                        placeholder="you@bakery.com"
                        :disabled="submitting" />

                    <label class="contact-dialog__label" for="landing-contact-message">Message</label>
                    <textarea
                        id="landing-contact-message"
                        v-model="message"
                        class="contact-dialog__textarea"
                        rows="5"
                        placeholder="How can we help?"
                        :disabled="submitting" />

                    <p v-if="error" class="contact-dialog__error" role="alert">{{ error }}</p>
                </template>
            </div>

            <div class="contact-dialog__actions">
                <button class="contact-dialog__action contact-dialog__action--secondary" type="button" :disabled="submitting" @click="close">
                    {{ hasSubmitted ? 'Close' : 'Cancel' }}
                </button>
                <button
                    v-if="!hasSubmitted"
                    class="contact-dialog__action contact-dialog__action--primary"
                    type="button"
                    :disabled="!canSubmit"
                    @click="submit">
                    <span v-if="submitting">Sending…</span>
                    <span v-else>Send</span>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .contact-dialog {
        &__overlay {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.55);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 18px;
            z-index: 50;
        }

        &__dialog {
            position: relative;
            width: 100%;
            max-width: 560px;
            border-radius: 18px;
            background: var(--l2-dialog-bg, var(--landing-bg, #ffffff));
            color: var(--l2-text, var(--landing-text, #0f172a));
            box-shadow: 0 26px 70px rgba(15, 23, 42, 0.28);
            padding: 22px 22px 18px;
            text-align: left;
            box-sizing: border-box;
            border: 1px solid var(--l2-dialog-border, var(--landing-border, rgba(226, 232, 240, 0.9)));

            *,
            *::before,
            *::after {
                box-sizing: border-box;
            }
        }

        &__close {
            position: absolute;
            top: 10px;
            right: 10px;
            height: 36px;
            width: 36px;
            border: 0;
            border-radius: 10px;
            background: transparent;
            font-size: 26px;
            line-height: 1;
            cursor: pointer;
            color: var(--l2-text-muted, var(--landing-muted, rgba(71, 85, 105, 0.9)));

            &:hover {
                background: var(--l2-accent-soft, rgba(226, 232, 240, 0.7));
            }
        }

        &__top {
            display: grid;
            justify-items: start;
            gap: 6px;
            padding: 8px 8px 0;
        }

        &__title {
            margin: 0;
            font-size: 20px;
            font-weight: 800;
            letter-spacing: -0.01em;
        }

        &__subtitle {
            margin: 0;
            color: var(--l2-text-muted, var(--landing-muted, #475569));
            font-size: 14px;
        }

        &__body {
            margin-top: 14px;
            border-top: 1px solid var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
            padding-top: 14px;
        }

        &__success {
            margin: 0;
            font-weight: 800;
            color: var(--l2-text, var(--landing-text, #0f172a));
        }

        &__label {
            display: block;
            margin: 10px 0 6px;
            font-size: 14px;
            font-weight: 700;
            color: var(--l2-text, var(--landing-text, #0f172a));
        }

        &__input {
            width: 100%;
            padding: 12px 12px;
            background: var(--l2-surface, #ffffff);
            color: var(--l2-text, var(--landing-text, #0f172a));
            border: 1px solid var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
            border-radius: 14px;
            font-size: 15px;
            box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
            outline: none;

            &:focus {
                border-color: rgba(114, 87, 209, 0.55);
                box-shadow:
                    0 10px 26px rgba(15, 23, 42, 0.05),
                    0 0 0 4px rgba(114, 87, 209, 0.12);
            }

            &:disabled {
                opacity: 0.7;
                cursor: not-allowed;
            }

            &::placeholder {
                color: var(--l2-text-subtle, rgba(100, 116, 139, 0.9));
            }
        }

        &__textarea {
            width: 100%;
            max-width: 100%;
            resize: vertical;
            min-height: 130px;
            padding: 12px 12px;
            background: var(--l2-surface, #ffffff);
            color: var(--l2-text, var(--landing-text, #0f172a));
            border: 1px solid var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
            border-radius: 14px;
            font-size: 15px;
            box-shadow: 0 10px 26px rgba(15, 23, 42, 0.05);
            outline: none;

            &:focus {
                border-color: rgba(114, 87, 209, 0.55);
                box-shadow:
                    0 10px 26px rgba(15, 23, 42, 0.05),
                    0 0 0 4px rgba(114, 87, 209, 0.12);
            }

            &:disabled {
                opacity: 0.7;
                cursor: not-allowed;
            }

            &::placeholder {
                color: var(--l2-text-subtle, rgba(100, 116, 139, 0.9));
            }
        }

        &__error {
            margin: 10px 0 0;
            font-size: 14px;
            color: #b91c1c;
        }

        &__actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 16px;
            padding-top: 12px;
            border-top: 1px solid var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
        }

        &__action {
            padding: 10px 14px;
            border-radius: 12px;
            font-weight: 800;
            font-size: 14px;
            cursor: pointer;
            border: 1px solid transparent;

            &--secondary {
                background: transparent;
                border-color: var(--landing-border, var(--l2-border-subtle, rgba(226, 232, 240, 0.9)));
                color: inherit;
            }

            &--primary {
                background: var(--landing-accent);
                color: #ffffff;
                border-color: var(--landing-accent);
            }

            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }
    }

    @media (max-width: 520px) {
        .contact-dialog {
            &__actions {
                flex-direction: column-reverse;
                align-items: stretch;
            }
        }
    }
</style>
